import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@loot-os/ui/components/alert-dialog";
import { Button } from "@loot-os/ui/components/button";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useTRPC } from "../lib/trpc";

/** Voids a whole batch after a confirmation: its unredeemed codes stop redeeming. */
export function VoidGiftCodeBatchButton({
  batchId,
  unredeemed,
}: {
  batchId: string;
  unredeemed: number;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const voidBatch = useMutation(
    trpc.giftCodes.voidBatch.mutationOptions({
      onSuccess: () => {
        toast.success("Batch voided.");
        setOpen(false);
        void queryClient.invalidateQueries({ queryKey: trpc.giftCodes.pathKey() });
      },
    }),
  );

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger render={<Button type="button" variant="destructive" />}>
        Void batch
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Void this batch?</AlertDialogTitle>
          <AlertDialogDescription>
            {unredeemed === 1 ? "The 1 code" : `The ${unredeemed} codes`} not redeemed yet will stop
            working. Codes already redeemed keep their credit. This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={voidBatch.isPending}
            onClick={() => voidBatch.mutate({ batchId })}
          >
            {voidBatch.isPending ? "Voiding…" : "Void batch"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Voids one code after a confirmation; the rest of its batch keeps working. */
export function VoidGiftCodeButton({ codeId, code }: { codeId: string; code: string }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const voidCode = useMutation(
    trpc.giftCodes.voidCode.mutationOptions({
      onSuccess: () => {
        toast.success("Code voided.");
        setOpen(false);
        void queryClient.invalidateQueries({ queryKey: trpc.giftCodes.pathKey() });
      },
    }),
  );

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger render={<Button type="button" variant="outline" size="sm" />}>
        Void
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Void <span className="font-mono">{code}</span>?
          </AlertDialogTitle>
          <AlertDialogDescription>
            The code will stop working. This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={voidCode.isPending}
            onClick={() => voidCode.mutate({ codeId })}
          >
            {voidCode.isPending ? "Voiding…" : "Void code"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
